import React, { ReactNode } from 'react';
import { Box, Paper, Typography, Button, Chip } from '@mui/material';
import { Lock as LockIcon, Upgrade as UpgradeIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTenant } from '../../../contexts/TenantContext';
import { PATHS } from '../../../routes/paths';

interface FeatureGateProps {
  feature: string;
  children: ReactNode;
  fallback?: ReactNode;
  showUpgrade?: boolean;
}

const FEATURE_NAMES: Record<string, string> = {
  VENDAS: 'PDV e Vendas',
  ESTOQUE: 'Controle de Estoque',
  RELATORIOS: 'Relatorios Avancados',
  DEVOLUCOES: 'Gestao de Devolucoes',
  CREDITO: 'Controle de Fiado',
  CUPONS: 'Sistema de Cupons',
  API: 'Acesso a API',
  MULTI_USUARIOS: 'Multiplos Usuarios',
  EXPORTACAO: 'Exportacao Excel/PDF',
  INTEGRACAO: 'Integracoes Externas',
};

export function FeatureGate({ feature, children, fallback, showUpgrade = true }: FeatureGateProps) {
  const { hasFeature, tenant } = useTenant();
  const navigate = useNavigate();

  if (hasFeature(feature)) {
    return <>{children}</>;
  }

  if (fallback) {
    return <>{fallback}</>;
  }

  if (!showUpgrade) {
    return null;
  }

  const featureName = FEATURE_NAMES[feature] || feature;
  const currentPlan = tenant?.assinatura?.plano?.nome || 'Basico';

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px',
        p: 3,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          textAlign: 'center',
          maxWidth: 500,
          borderRadius: 2,
        }}
      >
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            bgcolor: 'warning.light',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            mx: 'auto',
            mb: 3,
          }}
        >
          <LockIcon sx={{ fontSize: 40, color: 'warning.dark' }} />
        </Box>

        <Typography variant="h5" fontWeight="bold" gutterBottom>
          Recurso Bloqueado
        </Typography>

        <Typography color="text.secondary" sx={{ mb: 2 }}>
          A funcionalidade <strong>{featureName}</strong> nao esta disponivel no seu plano atual.
        </Typography>

        <Chip
          label={`Plano Atual: ${currentPlan}`}
          color="default"
          sx={{ mb: 3 }}
        />

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Faca upgrade do seu plano para desbloquear esta e outras funcionalidades.
        </Typography>

        <Button
          variant="contained"
          size="large"
          startIcon={<UpgradeIcon />}
          onClick={() => navigate(PATHS.SUBSCRIPTION)}
          sx={{ px: 4 }}
        >
          Ver Planos
        </Button>
      </Paper>
    </Box>
  );
}

export default FeatureGate;
